import { create } from "zustand";
import type { LLMModel } from "@/api/models/api-types";
import useAuthStore from "@/store/use-auth-store";
import { readUserState, writeUserState } from "@/utils/user-state";

interface LLMSelectionState {
  // Modal state
  isOpen: boolean;

  // Selection state - Simple approach
  selectedModelName: string | null; // ✅ Persist edilir
  selectedModel: LLMModel | null; // ✅ Runtime'da tutulur (persist edilmez)
  selectedProvider: string;

  // Filter & search state
  searchQuery: string;
  visibleItems: number;

  // Callback state
  onSelectCallback: ((model: LLMModel) => void) | null;

  // Actions - Modal management
  openModal: (onSelect?: (model: LLMModel) => void) => void;
  closeModal: (open?: boolean) => void;

  // Actions - Model selection
  selectModel: (model: LLMModel) => void;
  selectFirstModel: (models: LLMModel[]) => void;
  clearSelection: () => void;

  // Actions - Filtering
  selectProvider: (provider: string) => void;
  updateSearchQuery: (query: string) => void;
  resetFilters: () => void;

  // Actions - Pagination
  loadMoreItems: () => void;
  resetPagination: () => void;

  // Actions - Complete reset
  resetState: () => void;

  // Actions - Initialize from cache
  initializeFromCache: () => void;
}

export const useLLMSelectionStore = create<LLMSelectionState>()((set, get) => ({
  // Initial state
  isOpen: false,
  selectedModelName: null,
  selectedModel: null,
  selectedProvider: "all",
  searchQuery: "",
  visibleItems: 20,
  onSelectCallback: null,

  // Modal management
  openModal: (onSelect?: (model: LLMModel) => void) =>
    set({ isOpen: true, onSelectCallback: onSelect || null }),
  closeModal: (open?: boolean) => {
    // Radix UI Dialog onOpenChange boolean parametre gönderir
    // Eğer false ise (modal kapatılıyorsa), state'i güncelle
    if (open === false || open === undefined) {
      set({ isOpen: false, onSelectCallback: null });
    }
  },

  // Model selection
  selectModel: (model: LLMModel) => {
    const state = get();
    // Call callback if exists
    if (state.onSelectCallback) {
      state.onSelectCallback(model);
    }
    set({
      selectedModelName: model.name, // ✅ Model name persist edilir
      selectedModel: model, // ✅ Model objesi runtime'da tutulur
      isOpen: false,
      onSelectCallback: null,
    });

    const user = useAuthStore.getState().user;
    if (user?._id) {
      writeUserState(
        { userId: user._id, version: 2 },
        {
          llm: {
            selectedModelName: model.name,
            selectedProvider: get().selectedProvider,
          },
        }
      );
    }
  },

  selectFirstModel: (models: LLMModel[]) => {
    const firstActiveModel = models.find((model: LLMModel) => !model.isDeleted);
    if (firstActiveModel) {
      get().selectModel(firstActiveModel);
    } else {
    }
  },

  clearSelection: () => {
    set({
      selectedModelName: null,
      selectedModel: null,
    });

    // ✅ LLM seçimi temizlendiğinde localStorage'ı da güncelle
    const user = useAuthStore.getState().user;
    if (user?._id) {
      writeUserState(
        { userId: user._id, version: 2 },
        {
          llm: {
            selectedModelName: null,
            selectedProvider: get().selectedProvider,
          },
        }
      );
    }
  },

  // Filtering
  selectProvider: (provider: string) =>
    set({
      selectedProvider: provider,
      visibleItems: 20,
    }),
  updateSearchQuery: (query: string) =>
    set({
      searchQuery: query,
      visibleItems: 20,
    }),
  resetFilters: () =>
    set({
      selectedProvider: "all",
      searchQuery: "",
      visibleItems: 20,
    }),

  // Pagination
  loadMoreItems: () => {
    const state = get();
    set({
      visibleItems: state.visibleItems + 20,
    });
  },
  resetPagination: () => set({ visibleItems: 20 }),

  // Complete reset
  resetState: () => {
    set({
      selectedProvider: "all",
      searchQuery: "",
      visibleItems: 20,
      selectedModelName: null,
      selectedModel: null,
      isOpen: false,
    });

    // ✅ Reset durumunda localStorage'ı da güncelle
    const user = useAuthStore.getState().user;
    if (user?._id) {
      writeUserState(
        { userId: user._id, version: 2 },
        {
          llm: {
            selectedModelName: null,
            selectedProvider: "all",
          },
        }
      );
    }
  },

  // ✅ Initialize from localStorage cache
  initializeFromCache: () => {
    try {
      const user = useAuthStore.getState().user;
      
      if (!user?._id) {
        return;
      }

      const env = readUserState({ userId: user._id, version: 2 });
      const cached = env?.state?.llm;

      if (cached) {
        set({
          selectedModelName: cached.selectedModelName ?? null,
          selectedProvider: cached.selectedProvider ?? "all",
        });
      }
    } catch (error) {
      console.error("Error loading LLM cache:", error);
    }
  },
}));

// ❌ REMOVED: Auto-hydrate on module load
// Bu kod App.tsx'teki useEffect'te kontrollü şekilde yapılıyor
// Çift hydrate problemi yaratıyor, bu yüzden kaldırıldı
